<template>
  <b-container
    class="ticket-detail-bg py-3"
    aria-label="Détail du ticket de support logistique"
    role="main"
  >
    <b-row align-h="center">
      <b-col
        cols="12"
        class="p-0"
      >
        <!-- Action Buttons -->
        <div class="d-flex justify-content-end">
          <b-button
            v-if="!isClosed && canChangeStatus"
            variant="info"
            class="mr-2"
            :disabled="ticket && ticket.closed_at"
            @click="showStatusModal = true"
          >
            Changer le statut
          </b-button>
          <b-button
            v-if="isAdmin && !isClosed"
            variant="primary"
            @click="showAssignModal = true"
          >
            Assigner à un support
          </b-button>
          <b-button
            v-if="isAdmin"
            variant="danger"
            class="ml-2"
            :disabled="!ticket"
            @click="confirmDeleteTicket"
          >
            Supprimer
          </b-button>
        </div>

        <!-- Ticket Detail Card -->
        <b-card
          v-if="ticket"
          class="ticket-detail-card shadow-sm"
          aria-label="Carte de détail du ticket de support"
        >
          <div
            class="sr-only"
            aria-live="polite"
          >
            Vous consultez le détail d’un ticket de support logistique. Utilisez les boutons pour modifier, changer le statut ou assigner le ticket si vous avez les droits nécessaires.
          </div>
          <!-- Header -->
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex align-items-center">
              <b-button
                variant="link"
                class="p-0 mr-2"
                @click="$router.back()"
              >
                <img
                  src="@/assets/images/back.svg"
                  alt="Retour"
                  width="28"
                >
              </b-button>
              <!-- Only show the badge, not "Ticket #ref" and badge -->
              <b-badge
                v-b-tooltip.hover
                variant="light"
                class="ref-badge"
                title="Copier la référence"
                @click="copyReference(ticket.reference)"
              >
                <span>{{ ticket.reference }}</span>
                <span
                  v-if="copied"
                  class="ml-1 text-success"
                >✔</span>
              </b-badge>
            </div>
            <div class="d-flex flex-column align-items-end">
              <div class="d-flex align-items-center">
                <b-badge
                  :variant="statusVariant(ticket.status)"
                  class="status-badge"
                >
                  {{ ticket.status.display_name || ticket.status.name }}
                </b-badge>
                <b-button
                  v-if="canEditTicket && !isClosed"
                  variant="warning"
                  class="ml-2"
                  @click="goToEdit"
                >
                  Modifier
                </b-button>
              </div>
              <div
                v-if="ticket.closed_at"
                class="text-muted mt-1"
                style="font-size: 0.98rem;"
              >
                Clôturé le : {{ formatDate(ticket.closed_at) }}
              </div>
            </div>
          </div>

          <!-- Ticket Info -->
          <b-row class="mb-3">
            <b-col md="7">
              <div class="mb-2">
                <span class="font-weight-bold">Titre :</span>
                <span>{{ ticket.title }}</span>
              </div>
              <div class="mb-2">
                <span class="font-weight-bold">Catégorie :</span>
                <b-badge variant="info ml-1">
                  {{ ticket.category ? (ticket.category.display_name || ticket.category.name) : '-' }}
                </b-badge>
                <div
                  v-if="ticket.category && ticket.category.description"
                  class="category-description mt-1"
                >
                  {{ ticket.category.description }}
                </div>
              </div>
              <div class="mb-2">
                <span class="font-weight-bold">Créé le :</span>
                <span>{{ formatDate(ticket.created_at) }}</span>
              </div>
              <div class="mb-2">
                <span class="font-weight-bold">Dernière mise à jour :</span>
                <span>{{ formatDate(ticket.updated_at) }}</span>
              </div>
            </b-col>
            <b-col md="5">
              <div class="mb-2">
                <span class="font-weight-bold">Demandeur :</span>
                <b-avatar
                  :src="getAvatar(ticket.submitter)"
                  class="mr-1"
                  size="32"
                />
                <span>{{ ticket.submitter ? ticket.submitter.username : '-' }}</span>
                <span
                  v-if="ticket.submitter && ticket.submitter.phone"
                  class="ml-2 text-muted"
                >{{ ticket.submitter.phone }}</span>
              </div>
              <div class="mb-2">
                <span class="font-weight-bold">Assigné à :</span>
                <b-avatar
                  :src="getAvatar(ticket.assignee)"
                  class="mr-1"
                  size="32"
                />
                <span>{{ ticket.assignee ? ticket.assignee.username : '-' }}</span>
              </div>
            </b-col>
          </b-row>

          <!-- Description -->
          <b-card
            class="mb-3"
            body-class="p-3"
          >
            <h5 class="font-weight-bold mb-2">
              Description
            </h5>
            <div class="ticket-desc">
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="ticket && ticket.description"
                v-html="sanitizedDescription"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div v-else>
                -
              </div>
            </div>
          </b-card>

          <!-- Orders -->
          <b-card
            class="mb-3"
            body-class="p-3"
          >
            <h5 class="font-weight-bold mb-2">
              Commandes liées
            </h5>
            <b-table
              v-if="ticket.orders && ticket.orders.length"
              :items="ticket.orders"
              :fields="orderFields"
              small
              responsive
              head-variant="light"
              class="orders-table"
            >
              <template #cell(id)="{ item }">
                <b-badge variant="primary">
                  #{{ item.id }}
                </b-badge>
              </template>
              <template #cell(name)="{ item }">
                {{ item.name }}
              </template>
              <template #cell(phone1)="{ item }">
                <b-badge variant="secondary">
                  {{ item.phone1 }}
                </b-badge>
              </template>
              <template #cell(price_ttc)="{ item }">
                <span>{{ item.price_ttc }} TND</span>
              </template>
              <template #cell(status)="{ item }">
                <b-badge variant="info">
                  {{ getOrderStatusLabel(item.status) }}
                </b-badge>
              </template>
              <template #cell(date)="{ item }">
                {{ item.date }}
              </template>
            </b-table>
            <div
              v-else
              class="text-muted"
            >
              Aucune commande liée.
            </div>
          </b-card>

          <!-- Comments -->
          <b-card
            class="mb-3"
            body-class="p-3"
          >
            <h5 class="font-weight-bold mb-2">
              Commentaires
            </h5>
            <div v-if="ticket.comments && ticket.comments.length">
              <div
                v-for="comment in ticket.comments"
                :key="comment.id"
                class="comment-item mb-2"
              >
                <b-avatar
                  :src="getAvatar(comment.author)"
                  class="mr-2"
                  size="28"
                />
                <span class="font-weight-bold">{{ comment.author.username }}</span>
                <span class="text-muted ml-2">{{ formatDate(comment.created_at) }}</span>
                <div class="comment-text mt-1">
                  {{ comment.comment }}
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-muted"
            >
              Aucun commentaire.
            </div>
            <b-form
              v-if="canComment && !isClosed"
              class="mt-3"
              @submit.prevent="addComment"
            >
              <b-form-group label="Ajouter un commentaire">
                <b-form-textarea
                  v-model="commentText"
                  rows="2"
                  :disabled="commentLoading"
                  placeholder="Votre commentaire..."
                />
                <b-button
                  type="submit"
                  variant="success"
                  class="mt-2"
                  :disabled="commentLoading || !commentText.trim()"
                >
                  Ajouter
                </b-button>
                <div
                  v-if="commentError"
                  class="text-danger mt-1"
                >
                  {{ commentError }}
                </div>
              </b-form-group>
            </b-form>
          </b-card>

          <!-- History -->
          <b-card
            class="mb-3"
            body-class="p-3"
          >
            <h5 class="font-weight-bold mb-2">
              Historique
            </h5>
            <ul class="timeline">
              <li
                v-for="h in ticket.histories"
                :key="h.id"
                class="timeline-item"
              >
                <span class="timeline-dot" />
                <div class="timeline-content">
                  <div class="font-weight-bold">
                    {{ h.message }}
                  </div>
                  <div class="text-muted small">
                    {{ formatDate(h.created_at) }} - {{ h.description }}
                  </div>
                </div>
              </li>
            </ul>
          </b-card>
        </b-card>

        <!-- Status Change Modal -->
        <b-modal
          v-model="showStatusModal"
          title="Changer le statut"
          hide-footer
          :disabled="isClosed"
        >
          <b-form-group label="Nouveau statut">
            <b-form-select
              v-model="selectedStatus"
              :options="filteredStatusList"
              :disabled="statusLoading || isClosed"
              placeholder="Sélectionnez un statut"
            />
          </b-form-group>
          <b-button
            variant="info"
            :disabled="statusLoading || !selectedStatus || isClosed"
            @click="changeStatusModal"
          >
            Confirmer
          </b-button>
        </b-modal>

        <!-- Assign Modal -->
        <b-modal
          v-model="showAssignModal"
          title="Assigner à un support"
          hide-footer
          :disabled="isClosed"
        >
          <b-form-group label="Support">
            <b-form-select
              v-model="selectedAssignee"
              :options="staffList"
              :disabled="assignLoading || isClosed"
              placeholder="Sélectionnez un staff"
            />
          </b-form-group>
          <b-button
            variant="primary"
            :disabled="assignLoading || !selectedAssignee || isClosed"
            @click="assignTicketModal"
          >
            Confirmer
          </b-button>
        </b-modal>
        <div
          v-if="!ticket"
          class="text-center py-5"
        >
          <b-spinner
            variant="danger"
            label="Chargement..."
            class="mb-2"
          />
          <div>Chargement du ticket...</div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from '@/libs/axios'
import {
  BAvatar, BBadge, BButton, BCard, BTable, BSpinner, VBTooltip, BFormSelect, BFormTextarea, BFormGroup, BForm,
} from 'bootstrap-vue'
import defaultAvatar from '@/assets/images/avatars/user.png'
import storeAuth from '@/store/store'
import DOMPurify from 'dompurify'

export default {
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BAvatar, BBadge, BButton, BCard, BTable, BSpinner, BFormSelect, BFormTextarea, BFormGroup, BForm,
  },
  data() {
    return {
      ticket: null,
      copied: false,
      orderFields: [
        { key: 'id', label: 'BL' },
        { key: 'name', label: 'Client' },
        { key: 'phone1', label: 'Téléphone' },
        { key: 'price_ttc', label: 'Montant' },
        { key: 'status', label: 'Statut' },
        { key: 'date', label: 'Date' },
      ],
      user: storeAuth.state.user,
      staffList: [],
      statusList: [],
      assignLoading: false,
      statusLoading: false,
      selectedAssignee: null,
      selectedStatus: null,
      commentText: '',
      commentLoading: false,
      commentError: '',
      showStatusModal: false,
      showAssignModal: false,
    }
  },
  computed: {
    isClosed() {
      return !!(this.ticket && this.ticket.closed_at)
    },
    canEditTicket() {
      return (
        this.user?.role === 'supplier'
        && this.ticket
        && this.ticket.submitter
        && this.ticket.submitter.id === this.user.id
        && this.ticket.status && this.ticket.status.is_default
        && !this.isClosed
      )
    },
    canChangeStatus() {
      return (
        (this.user?.role === 'admin'
         || this.user?.role === 'manager'
         || this.user?.role === 'customer_support'
         || this.user?.role === 'warehouse_operator')
        && !this.isClosed
      )
    },
    canComment() {
      return !!this.user && !this.isClosed
    },
    isAdmin() {
      return this.user?.role === 'admin'
    },
    filteredStatusList() {
      // Exclude current status from options
      if (!this.statusList || !this.ticket) return []
      return this.statusList
        .filter(s => s.id !== this.ticket.status.id)
        .map(s => ({ value: s.id, text: s.display_name || s.name }))
    },
    sanitizedDescription() {
      if (!this.ticket || !this.ticket.description) return ''
      return DOMPurify.sanitize(this.ticket.description)
    },
  },
  watch: {
    ticket(newTicket) {
      if (
        this.user?.role === 'supplier'
        && newTicket
        && newTicket.submitter
        && newTicket.submitter.id !== this.user.id
      ) {
        this.$bvToast.toast('Vous n\'avez pas la permission de voir ce ticket.', { variant: 'danger' })
        this.$router.replace({ name: 'SupportTicketList' })
      }
    },
  },
  async mounted() {
    await this.fetchTicket()
    if (this.user.role === 'admin') {
      this.fetchStaffList()
    }
    if (this.canChangeStatus) {
      this.fetchStatusList()
    }
  },
  methods: {
    async confirmDeleteTicket() {
      const message = 'Confirmer la suppression définitive de ce ticket ? Cette action est irréversible.'
      try {
        let ok = true
        if (this.$bvModal && typeof this.$bvModal.msgBoxConfirm === 'function') {
          ok = await this.$bvModal.msgBoxConfirm(message, {
            title: 'Confirmation',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Supprimer',
            cancelTitle: 'Annuler',
            footerClass: 'p-2',
            centered: true,
          })
        } else {
          // Fallback
          // eslint-disable-next-line no-alert
          ok = window.confirm(message)
        }
        if (!ok) return
        await axios.delete(`/api/support/tickets/${this.ticket.id}/`)
        if (this.$bvToast) this.$bvToast.toast('Ticket supprimé avec succès.', { variant: 'success' })
        this.$router.replace({ name: 'SupportTicketList' })
      } catch (e) {
        const msg = (e.response && e.response.status === 403)
          ? 'Vous n\'avez pas la permission de supprimer ce ticket.'
          : 'Erreur lors de la suppression du ticket.'
        if (this.$bvToast) this.$bvToast.toast(msg, { variant: 'danger' })
      }
    },
    async fetchTicket() {
      this.ticket = null
      try {
        const { data } = await axios.get(`/api/support/tickets/${this.$route.params.id}/`)
        this.ticket = data
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$bvToast.toast('Vous n\'avez pas la permission de voir ce ticket.', { variant: 'danger' })
          this.$router.replace({ name: 'SupportTicketList' })
        } else {
          this.$bvToast.toast('Erreur lors du chargement du ticket.', { variant: 'danger' })
        }
      }
    },
    async fetchStaffList() {
      // Only fetch users with role customer_support
      const { data } = await axios.get('/api/auth/staff-members/?role=customer_support')
      const rawList = data.results || data
      this.staffList = rawList.map(user => ({ value: user.id, text: user.username }))
    },
    async fetchStatusList() {
      const { data } = await axios.get('/api/support/statuses/')
      this.statusList = data
    },
    async changeStatusModal() {
      if (!this.selectedStatus) return
      this.statusLoading = true
      try {
        await axios.patch(`/api/support/tickets/${this.ticket.id}/change_status/`, { status: this.selectedStatus })
        this.$bvToast.toast('Statut modifié avec succès.', { variant: 'success' })
        await this.fetchTicket()
        this.showStatusModal = false
      } catch (e) {
        this.$bvToast.toast('Erreur lors du changement de statut.', { variant: 'danger' })
      } finally {
        this.statusLoading = false
      }
    },
    async assignTicketModal() {
      if (!this.selectedAssignee) return
      this.assignLoading = true
      try {
        await axios.patch(`/api/support/tickets/${this.ticket.id}/`, { assignee_id: this.selectedAssignee })
        this.$bvToast.toast('Ticket assigné avec succès.', { variant: 'success' })
        await this.fetchTicket()
        this.showAssignModal = false
      } catch (e) {
        this.$bvToast.toast('Erreur lors de l\'assignation.', { variant: 'danger' })
      } finally {
        this.assignLoading = false
      }
    },
    async assignTicket() {
      if (!this.selectedAssignee) return
      this.assignLoading = true
      try {
        await axios.patch(`/api/support/tickets/${this.ticket.id}/`, { assignee_id: this.selectedAssignee })
        this.$bvToast.toast('Ticket assigné avec succès.', { variant: 'success' })
        await this.fetchTicket()
      } catch (e) {
        this.$bvToast.toast('Erreur lors de l\'assignation.', { variant: 'danger' })
      } finally {
        this.assignLoading = false
      }
    },
    async changeStatus() {
      if (!this.selectedStatus) return
      this.statusLoading = true
      try {
        await axios.patch(`/api/support/tickets/${this.ticket.id}/change_status/`, { status: this.selectedStatus })
        this.$bvToast.toast('Statut modifié avec succès.', { variant: 'success' })
        await this.fetchTicket()
      } catch (e) {
        this.$bvToast.toast('Erreur lors du changement de statut.', { variant: 'danger' })
      } finally {
        this.statusLoading = false
      }
    },
    async addComment() {
      if (!this.commentText.trim()) return
      this.commentLoading = true
      this.commentError = ''
      try {
        await axios.post('/api/support/comments/', {
          ticket: this.ticket.id,
          comment: this.commentText,
        })
        this.commentText = ''
        await this.fetchTicket()
      } catch (e) {
        this.commentError = 'Erreur lors de l\'ajout du commentaire.'
      } finally {
        this.commentLoading = false
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    statusVariant(status) {
      if (!status) return 'secondary'
      const map = {
        Ouvert: 'success',
        Fermé: 'danger',
        Résolu: 'primary',
        EnCours: 'warning',
      }
      return map[status.name] || 'secondary'
    },
    getAvatar(user) {
      // Use a default avatar or initials if no avatar available
      if (!user) return defaultAvatar
      // You can enhance this to use user.avatar if available
      return defaultAvatar
    },
    copyReference(ref) {
      if (!ref) return
      navigator.clipboard.writeText(ref)
      this.copied = true
      setTimeout(() => { this.copied = false }, 1200)
    },
    getOrderStatusLabel(status) {
      // Map status code to French label (matches backend Status IntegerChoices)
      const map = {
        0: 'En Attente',
        1: 'En Pickup',
        2: 'Colis au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour au fournisseur',
        8: 'En Transfert',
        9: 'Retour payé',
        10: 'Retour inter agence',
        11: 'Retour inter agence reçu',
        12: 'Colis annulé par admin',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'A rembourser',
        17: 'Remboursé',
      }
      return map[status] !== undefined ? map[status] : status
    },
    goToEdit() {
      this.$router.push({ name: 'SupportTicketEdit', params: { id: this.ticket.id } })
    },
  },
}
</script>

<style scoped>
.ticket-detail-bg {
  background: linear-gradient(135deg, #fff 60%, #ffeaea 100%);
  min-height: 100vh;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  /* Reduced padding */
}
.ticket-detail-card {
  border-radius: 0.75rem;
  border: none;
  background: #fff;
  padding: 1.5rem 1rem;
  /* Reduced padding */
  width: 100%;
  max-width: 100vw;
  margin: 0;
}
.ref-badge {
  background: linear-gradient(90deg, #00cfff 0%, #e80000 100%);
  color: #fff;
  font-weight: 700;
  border-radius: 8px;
  padding: 0.35em 0.8em;
  letter-spacing: 1px;
  cursor: pointer;
  font-size: 1.15rem;
}
.status-badge {
  font-size: 1.05rem;
  font-weight: 700;
  padding: 0.3em 0.8em;
  border-radius: 8px;
  text-transform: uppercase;
}
.ticket-desc {
  font-size: 1.08rem;
  color: #1d1d1b;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 0.7rem 1rem;
}
.orders-table {
  font-size: 0.97rem;
}
.comment-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
}
.comment-text {
  margin-left: 2.5rem;
  font-size: 1.01rem;
}
.timeline {
  list-style: none;
  padding-left: 0;
  margin-left: 0;
  position: relative;
}
.timeline-item {
  position: relative;
  margin-bottom: 1.2rem;
  padding-left: 2rem;
}
.timeline-dot {
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 12px;
  height: 12px;
  background: #e80000;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #e8000033;
}
.timeline-content {
  margin-left: 1.5rem;
}
.category-description {
  background: #f8f9fa;
  border-left: 3px solid #e80000;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  color: #1d1d1b;
  font-size: 0.97rem;
  display: flex;
  align-items: center;
  margin-top: 0.25rem;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
